import { useEffect, useState } from "react";
import { AlertTriangle, Check, CheckCircle2, Download, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { downloadInvoicePdf } from "../../lib/download";
import Button from "../../components/ui/Button";
import ShuwaBalance from "../../components/business/ShuwaBalance";
import MomoPhoneField from "../../components/MomoPhoneField";
import { useAuth } from "../../context/AuthContext";
import { Card, Badge } from "../../components/ui/Card";
import { formatPrice } from "../../lib/utils";

// Mirrors shuwa.app/for-business/pricing.
const FREE_FEATURES = ["Business profile", "Marketplace listing", "Basic booking", "Basic customer management", "Payment integration", "2% on bookings paid at the store", "1 staff member"];
const PRO_FEATURES = ["Everything in Free", "More advanced management", "Analytics", "Customer history", "Promotions", "0% on bookings paid at the store", "6 staff included"];

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "—";

/**
 * The owner's SHUWA plan: Free, or Pro billed monthly (with auto-renew,
 * due-date reminders, an invoice per period, and the shop hidden if a
 * payment stays missed).
 */
export default function Subscription() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [config, setConfig] = useState({ testMode: false });
  const [chosenProvider, setProvider] = useState("mtn");
  const [chosenPeriod, setPeriod] = useState(null);
  const [phone, setPhone] = useState("");
  const [testOutcome, setTestOutcome] = useState("success");
  const [paying, setPaying] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/owner/subscription").then(({ data }) => {
      setSub(data);
      if (data.provider) setProvider(data.provider);
    });
    api
      .get("/payments/config")
      .then(({ data }) =>
        setConfig({
          testMode: Boolean(data.test_mode),
          mobileMoney: data.mobile_money,
        })
      )
      .catch(() => {});
  }, []);

  if (!sub) return <p className="text-muted-foreground">Loading…</p>;

  const isPro = sub.plan === "pro";
  // Pro is paid by the month or by the week; renewals repeat the last choice.
  const period = chosenPeriod || sub.period || "monthly";
  const quote = sub.plans?.[period] || sub.quote;
  const monthly = sub.plans?.monthly || sub.quote;
  const providers = [
    ...(config.mobileMoney?.mtn !== false ? [{ id: "mtn", label: "MTN Mobile Money" }] : []),
    ...(config.mobileMoney?.airtel !== false ? [{ id: "airtel", label: "Airtel Money" }] : []),
    ...(config.testMode ? [{ id: "test", label: "Test payment" }] : []),
  ];
  // Fall back to the first option when the saved one isn't offered (e.g. MTN not live yet).
  const provider = providers.some((p) => p.id === chosenProvider) ? chosenProvider : providers[0]?.id;

  const pay = async () => {
    setPaying(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await api.post("/owner/subscription/pay", {
        provider,
        period,
        phone: ["mtn", "airtel"].includes(provider) && phone ? phone : undefined,
        test_outcome: provider === "test" ? testOutcome : undefined,
      });
      setSub(data.subscription);
      if (data.payment?.status === "failed") setError(data.message);
      else setMessage(data.message);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Payment failed.");
      if (e?.response?.data?.subscription) setSub(e.response.data.subscription);
    } finally {
      setPaying(false);
    }
  };

  const toggleAutoRenew = async () => {
    const { data } = await api.put("/owner/subscription", { auto_renew: !sub.auto_renew });
    setSub(data);
  };

  const downgrade = async () => {
    if (!window.confirm("Switch to the Free plan now? Pro features stop right away and staff beyond 1 are deactivated.")) return;
    const { data } = await api.post("/owner/subscription/downgrade");
    setSub(data);
    setMessage("You're now on the Free plan. Your shop is still listed.");
  };

  const methodLabel = providers.find((p) => p.id === sub.provider)?.label || sub.provider;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Your SHUWA plan</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every plan lists your shop on the marketplace. Pro unlocks advanced tools for growing teams.
        </p>
      </div>

      {sub.on_hold && (
        <div className="flex gap-3 rounded-2xl border border-red-300 bg-red-50 p-4 text-sm text-red-900">
          <EyeOff className="h-5 w-5 shrink-0" aria-hidden="true" />
          <p>
            <span className="font-semibold">Your shop is hidden from customers.</span>{" "}
            {sub.hold_reason === "statement"
              ? "A SHUWA statement is long overdue. Pay it below and your shop is back online straight away."
              : "Your Pro payment was not received. Pay below, or switch to the Free plan, and your shop is back online straight away."}{" "}
            Nothing has been deleted.
          </p>
        </div>
      )}

      {sub.status === "grace" && (
        <div className="flex gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
          <p>
            Your Pro payment is overdue. Pay before {formatDate(sub.hides_at)} — after that your shop is hidden from
            customers until you pay or switch to the Free plan.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <PlanCard name="Free" price={0} features={FREE_FEATURES} current={!isPro} />
        <PlanCard name="Pro" price={monthly.base} weeklyPrice={sub.plans?.weekly?.base} features={PRO_FEATURES} current={isPro} highlighted />
      </div>

      {isPro && (
        <Card className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-semibold text-slate-900">SHUWA Pro</p>
            <Badge variant={["grace", "lapsed"].includes(sub.status) ? "warning" : "accent"}>
              {sub.status === "exempt"
                ? "Included by SHUWA"
                : sub.status === "lapsed"
                ? "Unpaid"
                : sub.status === "grace"
                ? "Overdue"
                : "Active"}
            </Badge>
          </div>
          {sub.status !== "exempt" && (
            <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-muted-foreground">Next payment</dt>
                <dd className="font-semibold text-slate-900">{formatDate(sub.expires_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Days left</dt>
                <dd className="font-semibold text-slate-900">{sub.days_left != null ? Math.max(0, sub.days_left) : "—"}</dd>
              </div>
            </dl>
          )}
        </Card>
      )}

      {sub.status !== "exempt" && (
        <Card className="p-5">
          <p className="font-semibold text-slate-900">
            {isPro ? `Pay your next ${period === "weekly" ? "week" : "month"}` : "Upgrade to Pro"} — {formatPrice(quote.total)}
          </p>
          {sub.plans && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {[
                { id: "monthly", label: "Monthly" },
                { id: "weekly", label: "Weekly" },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPeriod(p.id)}
                  className={`rounded-xl border p-3 text-left text-sm ${period === p.id ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  <span className="font-medium text-slate-900">{p.label}</span>
                  <span className="block text-muted-foreground">
                    {formatPrice(sub.plans[p.id].total)} / {p.id === "weekly" ? "week" : "month"}
                  </span>
                </button>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            If a Pro payment is {sub.grace_days} days late, your shop is put on hold until you pay or switch to Free.
          </p>
          <div className="mt-2 space-y-1 text-sm text-muted-foreground">
            <div className="flex justify-between">
              <span>Pro plan ({quote.included_staff} staff included)</span>
              <span>{formatPrice(quote.base)}</span>
            </div>
            {quote.extra_staff > 0 && (
              <div className="flex justify-between">
                <span>
                  {quote.extra_staff} extra staff × {formatPrice(quote.extra_staff_price)}
                </span>
                <span>{formatPrice(quote.extra_total)}</span>
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {providers.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setProvider(p.id)}
                className={`rounded-xl border p-3 text-left text-sm ${provider === p.id ? "border-primary bg-primary/5" : "border-border"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {["mtn", "airtel"].includes(provider) && (
            <MomoPhoneField accountPhone={sub.phone || user?.phone} value={phone} onChange={setPhone} className="mt-3" />
          )}
          {provider === "test" && (
            <div className="mt-3 flex gap-2">
              {["success", "failed"].map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setTestOutcome(o)}
                  className={`flex-1 rounded-xl border p-2 text-sm ${testOutcome === o ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  {o === "success" ? "Approve" : "Decline"}
                </button>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            This method is also used for auto-renewal. Paying in cash or by bank transfer? Contact support@shuwa.app.
          </p>
          {message && (
            <p className="mt-3 flex items-center gap-2 rounded-xl bg-accent p-3 text-sm text-accent-foreground">
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" /> {message}
            </p>
          )}
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
          <Button className="mt-4" onClick={pay} disabled={paying || sub.has_pending_payment}>
            {paying
              ? "Processing…"
              : sub.has_pending_payment
              ? "Waiting for approval on your phone…"
              : `Pay ${formatPrice(quote.total)}`}
          </Button>
        </Card>
      )}

      {isPro && sub.status !== "exempt" && (
        <Card className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold text-slate-900">Auto-renew</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {sub.auto_renew && sub.provider
                  ? `We'll charge ${methodLabel} on the due date. We remind you 7, 3 and 1 days before.`
                  : "Off — we'll remind you before the due date so you can pay yourself."}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={sub.auto_renew}
              aria-label="Auto-renew"
              onClick={toggleAutoRenew}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${sub.auto_renew ? "bg-primary" : "bg-slate-300"}`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${sub.auto_renew ? "left-[22px]" : "left-0.5"}`}
              />
            </button>
          </div>
          <button type="button" onClick={downgrade} className="mt-4 text-sm font-medium text-muted-foreground underline">
            Switch to the Free plan
          </button>
        </Card>
      )}

      <ShuwaBalance config={config} onPaid={() => api.get("/owner/subscription").then(({ data }) => setSub(data))} />

      {sub.invoices?.length > 0 && (
        <div>
          <h2 className="mb-2 font-montserrat text-lg font-bold text-slate-900">Pro invoices</h2>
          <div className="divide-y divide-border rounded-2xl border border-border text-sm">
            {sub.invoices.map((invoice) => (
              <div key={invoice.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
                <span className="font-medium text-slate-900">{invoice.number}</span>
                <span className="text-muted-foreground">
                  {invoice.status === "paid" ? `Paid ${formatDate(invoice.paid_at)}` : `Due ${formatDate(invoice.due_at)}`}
                </span>
                <span className="font-medium text-slate-900">{formatPrice(invoice.amount)}</span>
                <Badge variant={invoice.status === "paid" ? "accent" : invoice.status === "void" ? "dark" : "warning"}>
                  {invoice.status === "paid" ? "Paid" : invoice.status === "void" ? "Cancelled" : "Due"}
                </Badge>
                <button
                  type="button"
                  onClick={() => downloadInvoicePdf(invoice)}
                  className="flex items-center gap-1 font-medium text-primary"
                >
                  <Download className="h-4 w-4" aria-hidden="true" /> PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {sub.payments?.length > 0 && (
        <div>
          <h2 className="mb-2 font-montserrat text-lg font-bold text-slate-900">Pro payment history</h2>
          <div className="divide-y divide-border rounded-2xl border border-border text-sm">
            {sub.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 p-3">
                <span>{formatDate(p.created_at)}</span>
                <span className="text-muted-foreground">{p.is_auto_renewal ? "Auto-renewal" : p.provider}</span>
                <span className="font-medium text-slate-900">{formatPrice(p.amount)}</span>
                <Badge variant={p.status === "success" ? "accent" : p.status === "failed" ? "dark" : "warning"}>
                  {p.status === "success" ? "Paid" : p.status === "failed" ? "Failed" : "Pending"}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PlanCard({ name, price, weeklyPrice, features, current, highlighted }) {
  return (
    <Card className={`p-5 ${highlighted ? "border-primary/40" : ""} ${current ? "ring-2 ring-primary" : ""}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold uppercase tracking-wide text-primary">{name}</p>
        {current && <Badge variant="accent">Current plan</Badge>}
      </div>
      <p className="mt-2 font-montserrat text-2xl font-extrabold text-slate-900">
        {formatPrice(price)} <span className="text-sm font-normal text-muted-foreground">/ month</span>
      </p>
      {weeklyPrice > 0 && <p className="text-sm text-muted-foreground">or {formatPrice(weeklyPrice)} / week</p>}
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2">
            <Check className="h-4 w-4 text-primary" aria-hidden="true" /> {f}
          </li>
        ))}
      </ul>
    </Card>
  );
}
